import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

const API_BASE = import.meta.env.VITE_API_BASE_URL || ''

/** Matches `services/skill_validation.MAX_SIZE_BYTES` (5 MB). */
export const SKILL_MAX_BYTES = 5120 * 1024

type Step = 1 | 2 | 3

type ValidatePayload = {
  valid?: boolean
  errors?: string[]
  warnings?: string[]
  frontmatter?: Record<string, unknown>
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

type UploadSkillWizardProps = {
  onClose: () => void
  onComplete: (skillId: string, displayName: string) => void
  onOpenAssign: (skillId: string, skillName: string) => void
  onPasteInstead?: () => void
}

export function UploadSkillWizard({ onClose, onComplete, onOpenAssign, onPasteInstead }: UploadSkillWizardProps) {
  const [step, setStep] = useState<Step>(1)
  const [file, setFile] = useState<File | null>(null)
  const [validating, setValidating] = useState(false)
  const [vr, setVr] = useState<ValidatePayload | null>(null)
  const [uploading, setUploading] = useState(false)
  const [doneId, setDoneId] = useState<string | null>(null)
  const [doneName, setDoneName] = useState('')
  const [doneDate, setDoneDate] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const runValidate = useCallback(async (f: File) => {
    setValidating(true)
    setVr(null)
    try {
      const fd = new FormData()
      fd.append('file', f)
      const res = await fetch(`${API_BASE}api/skills/validate`, { method: 'POST', credentials: 'include', body: fd })
      const data = (await res.json().catch(() => ({}))) as ValidatePayload
      if (!res.ok) {
        toast.error('Validation request failed')
        setVr({ valid: false, errors: ['Request failed'], warnings: [] })
        return
      }
      setVr(data)
    } catch {
      toast.error('Validation failed')
      setVr({ valid: false, errors: ['Network error'], warnings: [] })
    } finally {
      setValidating(false)
    }
  }, [])

  useEffect(() => {
    if (step === 2 && file) void runValidate(file)
  }, [step, file, runValidate])

  const pickFile = (f: File | null) => {
    if (!f) return
    if (f.size > SKILL_MAX_BYTES) {
      toast.error(`File exceeds max ${formatBytes(SKILL_MAX_BYTES)}`)
      return
    }
    setFile(f)
    setVr(null)
  }

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    pickFile(e.dataTransfer.files[0] ?? null)
  }

  const goUpload = async () => {
    if (!file || !vr?.valid) return
    setUploading(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await fetch(`${API_BASE}api/skills/upload`, { method: 'POST', credentials: 'include', body: fd })
      const data = (await res.json().catch(() => ({}))) as {
        skill_id?: string
        name?: string
        errors?: string[]
      }
      if (!res.ok) {
        toast.error(Array.isArray(data.errors) ? data.errors.join(' ') : 'Upload failed')
        return
      }
      const id = data.skill_id || ''
      const nm = data.name || (file.name || '').replace(/\.(skill|zip|md)$/i, '') || 'skill'
      setDoneId(id)
      setDoneName(nm)
      setDoneDate(
        new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
      )
      setStep(3)
      onComplete(id, nm)
    } catch {
      toast.error('Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const copyId = async () => {
    if (!doneId) return
    try {
      await navigator.clipboard.writeText(doneId)
      toast.success('Copied')
    } catch {
      toast.error('Could not copy')
    }
  }

  const stepper = (
    <div className={`suw-stepper${step === 3 ? ' suw-stepper--all-done' : ''}`} aria-hidden="true">
      <div className={`suw-step${step > 1 || step === 3 ? ' done' : ''}${step === 1 ? ' active' : ''}`}>
        <span className="suw-step-ico">{step > 1 || step === 3 ? '✓' : '1'}</span>
        <span className="suw-step-txt">Select file</span>
      </div>
      <div className="suw-step-line" />
      <div className={`suw-step${step > 2 || step === 3 ? ' done' : ''}${step === 2 ? ' active' : ''}`}>
        <span className="suw-step-ico">{step > 2 || step === 3 ? '✓' : '2'}</span>
        <span className="suw-step-txt">Validate</span>
      </div>
      <div className="suw-step-line" />
      <div className={`suw-step${step === 3 ? ' done active' : ''}`}>
        <span className="suw-step-ico">{step === 3 ? '✓' : '3'}</span>
        <span className="suw-step-txt">Upload</span>
      </div>
    </div>
  )

  return (
    <div className="modal-scrim suw-scrim" role="dialog" aria-modal="true" aria-labelledby="suwTitle" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal create-modal suw-modal">
        <div className="modal-head suw-head">
          <div>
            <div className="modal-title" id="suwTitle">
              Upload skill
            </div>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 14 14" fill="none">
              <path d="M2 2L12 12M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {stepper}

        <div className="modal-body suw-body">
          {step === 1 && (
            <>
              <input ref={inputRef} type="file" accept=".skill,.md,.zip" className="suw-file-input" onChange={e => pickFile(e.target.files?.[0] ?? null)} />
              <div
                className="suw-drop"
                onDragOver={e => e.preventDefault()}
                onDrop={onDrop}
                onClick={() => inputRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={e => e.key === 'Enter' && inputRef.current?.click()}
              >
                <div className="suw-drop-ico" aria-hidden="true">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <path d="M12 5v12M12 5l4 4M12 5L8 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M5 19h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  </svg>
                </div>
                <div className="suw-drop-title">Drop your .skill file here</div>
                <div className="suw-drop-sub">or click to browse · max {formatBytes(SKILL_MAX_BYTES)}</div>
              </div>
              {file && (
                <div className="suw-file-pill">
                  <span className="suw-file-name">{file.name}</span>
                  <span className="suw-file-size">{formatBytes(file.size)}</span>
                </div>
              )}
              <p className="suw-hint">
                Layout: <strong>your-skill-name/</strong> with <strong>SKILL.md</strong> (exact name, case-sensitive), optional{' '}
                <strong>scripts/</strong>, <strong>references/</strong>, <strong>assets/</strong>. YAML must include{' '}
                <strong>name</strong> (kebab-case, same as folder) and <strong>description</strong> (what it does and when to use it; no XML-style angle brackets). No{' '}
                <strong>README.md</strong> inside the skill folder.
              </p>
              {onPasteInstead && (
                <button type="button" className="suw-link" onClick={onPasteInstead}>
                  Or paste SKILL.md instead
                </button>
              )}
              <div className="suw-footer">
                <button type="button" className="btn-cancel" onClick={onClose}>
                  Cancel
                </button>
                <button type="button" className="btn-create" disabled={!file} onClick={() => setStep(2)}>
                  Next — validate
                </button>
              </div>
            </>
          )}

          {step === 2 && file && (
            <>
              <div className="suw-file-card">
                <span className="suw-file-card-ico" aria-hidden="true">
                  📄
                </span>
                <div>
                  <div className="suw-file-card-name">{file.name}</div>
                  <div className="suw-file-card-meta">
                    {formatBytes(file.size)}
                    {validating ? ' · Validating…' : ''}
                  </div>
                </div>
              </div>

              {!validating && vr && (
                <ul className="suw-checklist">
                  {vr.valid && (
                    <>
                      <li className="ok">
                        <span className="suw-ci">✓</span> SKILL.md present
                      </li>
                      <li className="ok">
                        <span className="suw-ci">✓</span> YAML frontmatter valid — name &amp; description found
                      </li>
                    </>
                  )}
                  <li className={file.size <= SKILL_MAX_BYTES ? 'ok' : 'bad'}>
                    <span className="suw-ci">{file.size <= SKILL_MAX_BYTES ? '✓' : '✕'}</span> File size within limit ({formatBytes(file.size)} / {formatBytes(SKILL_MAX_BYTES)})
                  </li>
                  {!vr.valid &&
                    (vr.errors || []).map((e, i) => (
                      <li key={`e${i}`} className="bad">
                        <span className="suw-ci">✕</span> {e}
                      </li>
                    ))}
                  {(vr.warnings || []).map((w, i) => (
                    <li key={`w${i}`} className="warn">
                      <span className="suw-ci">!</span> {w}
                    </li>
                  ))}
                </ul>
              )}

              {vr?.valid && (
                <div className={`suw-banner${(vr.warnings?.length || 0) > 0 ? ' warn' : ''}`}>
                  <span className="suw-banner-ico">✓</span>
                  {(vr.warnings?.length || 0) > 0
                    ? `Validation passed with ${vr.warnings!.length} warning(s). Ready to upload.`
                    : 'Validation passed. Ready to upload.'}
                </div>
              )}

              <div className="suw-footer">
                <button type="button" className="btn-cancel" onClick={() => setStep(1)}>
                  Back
                </button>
                <button type="button" className="btn-create" disabled={!vr?.valid || validating || uploading} onClick={goUpload}>
                  {uploading ? 'Uploading…' : 'Upload skill'}
                </button>
              </div>
            </>
          )}

          {step === 3 && doneId && (
            <>
              <div className="suw-success-banner">
                <span className="suw-banner-ico">✓</span>
                <strong>{doneName}</strong> uploaded successfully on {doneDate}.
              </div>
              <div className="suw-id-label">Skill ID — use this in your API calls</div>
              <div className="suw-id-row">
                <code className="suw-id-code">{doneId}</code>
                <button type="button" className="suw-copy" onClick={copyId}>
                  Copy
                </button>
              </div>
              <p className="suw-next-hint">Next step: assign this skill to users, or continue in the list.</p>
              <div className="suw-footer">
                <button type="button" className="btn-cancel" onClick={() => onOpenAssign(doneId, doneName)}>
                  Assign users
                </button>
                <button type="button" className="btn-cancel" onClick={onClose}>
                  Done
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
